import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Users } from 'lucide-react';
import PageLoader from '../components/PageLoader';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePaginatedFetch } from '../hooks/usePaginatedFetch';
import LoadMoreButton from '../components/LoadMoreButton';
import type { Challenge } from '@crewcast/shared';
import { ChallengeStatus } from '@crewcast/shared';
import { linkify } from '../utils/linkify';
import '../styles/challenges.css';

type EditableChallengeFields = Pick<
  Challenge,
  'title' | 'description' | 'rules' | 'points' | 'collaboratorsEnabled' | 'status'
>;

export default function ChallengesPage() {
  usePageTitle('Moments');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [createError, setCreateError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [createForm, setCreateForm] = useState<EditableChallengeFields>({
    title: '',
    description: '',
    rules: '',
    points: 0,
    collaboratorsEnabled: false,
    status: ChallengeStatus.ACTIVE,
  });
  const [pointsInput, setPointsInput] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  const navigate = useNavigate();
  const { token, user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const pointsEnabled = currentUser?.companySettings?.pointsEnabled ?? true;

  const {
    items: challenges,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    refresh,
  } = usePaginatedFetch<Challenge>({
    url: `${authConfig.apiUrl}/api/admin/challenges`,
    token,
    dataKey: 'challenges',
    limit: 20,
  });

  const handleCreateChallenge = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');

    // Prevent double submission
    if (creating || uploadingImage) {
      return;
    }

    // Validate points (skip when rewards are not active — default to 0)
    const points = pointsEnabled ? Number(pointsInput) : 0;
    if (pointsEnabled && (isNaN(points) || points < 0 || pointsInput === '')) {
      setCreateError('Please enter a valid positive number for points');
      return;
    }

    setCreating(true);

    try {
      if (!token) throw new Error('Not authenticated');

      let imageUrl: string | undefined;

      // Upload image if selected
      if (imageFile) {
        setUploadingImage(true);
        const presignedResponse = await fetch(
          `${authConfig.apiUrl}/api/upload/presigned-url`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              fileName: imageFile.name,
              fileType: imageFile.type,
              purpose: 'challenge',
            }),
          }
        );

        if (!presignedResponse.ok) {
          throw new Error('Failed to get upload URL');
        }

        const { uploadUrl, publicUrl } = await presignedResponse.json();

        // Upload to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: imageFile,
          headers: {
            'Content-Type': imageFile.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload image');
        }

        imageUrl = publicUrl;
        setUploadingImage(false);
      }

      // Create challenge
      const response = await fetch(`${authConfig.apiUrl}/api/admin/challenges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...createForm,
          points: points,
          image: imageUrl,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create challenge');
      }

      if (data.challenge) {
        refresh();
      }

      // Show success message
      setCreateSuccess(true);

      // Reset form
      setCreateForm({
        title: '',
        description: '',
        rules: '',
        points: 0,
        collaboratorsEnabled: false,
        status: ChallengeStatus.ACTIVE,
      });
      setPointsInput('');
      setImageFile(null);
      setImagePreview('');

      refresh();

      // Hide form after 2 seconds
      setTimeout(() => {
        setShowCreateForm(false);
        setCreateSuccess(false);
      }, 2000);
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create challenge');
    } finally {
      setCreating(false);
      setUploadingImage(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const validVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-m4v'];
      
      if (!validImageTypes.includes(file.type) && !validVideoTypes.includes(file.type)) {
        alert('Please upload an image (JPG, PNG, GIF, WEBP) or video (MP4, MOV)');
        return;
      }

      // Validate file size (100MB max)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        alert(`File size must be less than 100MB (current: ${Math.round(file.size / (1024 * 1024))}MB)`);
        return;
      }

      setImageFile(file);
      if (file.type.startsWith('video/')) {
        setImagePreview(URL.createObjectURL(file));
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  if (loading) {
    return (
      <div className="challenges-container">
        <PageLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="challenges-container">
        <div className="challenges-error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="challenges-container">
      <div className="challenges-header">
        <h1>Moments</h1>
        {isAdmin && (
          <button
            className="create-challenge-btn"
            onClick={() => {
              setShowCreateForm(!showCreateForm);
              setCreateSuccess(false);
              setCreateError('');
            }}
          >
            {showCreateForm ? 'Cancel' : '+ Create Moment'}
          </button>
        )}
      </div>

      {showCreateForm && isAdmin && (
        <div className="create-challenge-form">
          <h2>Create New Moment</h2>
          <form onSubmit={handleCreateChallenge}>
            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                value={createForm.title}
                onChange={(e) =>
                  setCreateForm({ ...createForm, title: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={createForm.description}
                onChange={(e) =>
                  setCreateForm({ ...createForm, description: e.target.value })
                }
                rows={4}
              />
            </div>

            <div className="form-group">
              <label htmlFor="rules">Rules</label>
              <textarea
                id="rules"
                value={createForm.rules}
                onChange={(e) =>
                  setCreateForm({ ...createForm, rules: e.target.value })
                }
                rows={4}
                placeholder="Moment rules and requirements..."
              />
            </div>

            {pointsEnabled && (
            <div className="form-group">
              <label htmlFor="points">Points *</label>
              <input
                type="text"
                id="points"
                value={pointsInput}
                onChange={(e) => {
                  // Only allow digits
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setPointsInput(value);
                }}
                placeholder="0"
                required
              />
            </div>
            )}

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={createForm.collaboratorsEnabled}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      collaboratorsEnabled: e.target.checked,
                    })
                  }
                />
                <span>Enable Collaborators (Team Moment)</span>
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={createForm.status}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    status: e.target.value as ChallengeStatus,
                  })
                }
                className="status-select"
              >
                <option value={ChallengeStatus.DRAFT}>Draft</option>
                <option value={ChallengeStatus.ACTIVE}>Active</option>
                <option value={ChallengeStatus.COMPLETE}>Complete</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="image">Moment Image or Video</label>
              <p className="field-hint">
                Upload an image or video (max 100MB). Supported formats: JPG, PNG, GIF, WEBP, MP4, MOV
              </p>
              <input
                type="file"
                id="image"
                accept="image/*,video/mp4,video/quicktime,video/x-m4v"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="image-preview">
                  {imageFile?.type.startsWith('video/') ? (
                    <video src={imagePreview} controls playsInline className="preview-video" />
                  ) : (
                    <img src={imagePreview} alt="Preview" />
                  )}
                </div>
              )}
            </div>

            {createError && <div className="form-error">{createError}</div>}
            {createSuccess && (
              <div className="form-success">
                ✓ Moment created successfully!
              </div>
            )}

            <div className="form-actions">
              <button
                type="submit"
                disabled={uploadingImage || creating}
                className="submit-btn"
              >
                {uploadingImage ? 'Uploading Media...' : creating ? 'Creating...' : 'Create Moment'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setCreateSuccess(false);
                  setCreateError('');
                  setCreateForm({
                    title: '',
                    description: '',
                    rules: '',
                    points: 0,
                    collaboratorsEnabled: false,
                    status: ChallengeStatus.ACTIVE,
                  });
                  setPointsInput('');
                  setImageFile(null);
                  setImagePreview('');
                }}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="challenges-list">
        {challenges.length === 0 ? (
          <div className="no-challenges">
            <p>No moments yet.</p>
            {isAdmin && <p>Create your first moment to get started!</p>}
          </div>
        ) : (
          <div className="challenges-grid">
            {challenges.map((challenge) => (
              <div key={challenge.challengeId} className="challenge-card">
                {challenge.image && (
                  <div className="challenge-image">
                    {challenge.image.match(/\.(mp4|mov|m4v)$/i) ? (
                      <video src={`${challenge.image}#t=0.001`} className="challenge-video" muted playsInline preload="metadata" />
                    ) : (
                      <img src={challenge.image} alt={challenge.title} />
                    )}
                  </div>
                )}
                  <div className="challenge-content">
                  <div className="challenge-title-row">
                    <h3>{challenge.title}</h3>
                    {/* Employees only ever see active Moments, so the badge is redundant for them */}
                    {isAdmin && (
                      <span className={`challenge-status-badge status-${challenge.status}`}>
                        {challenge.status === 'active'
                          ? 'Active'
                          : challenge.status === 'complete'
                          ? 'Complete'
                          : 'Draft'}
                      </span>
                    )}
                  </div>
                  {challenge.description && (
                    <p className="challenge-description">
                      {linkify(
                        challenge.description.length > 150
                          ? `${challenge.description.substring(0, 150)}...`
                          : challenge.description
                      )}
                    </p>
                  )}
                  <div className="challenge-meta">
                    {challenge.collaboratorsEnabled && (
                      <span className="collaborators-badge" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Users size={16} /> Team Moment
                      </span>
                    )}
                    {(challenge.points ?? 0) > 0 && (
                      <span className="points-badge" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Trophy size={16} /> {challenge.points} points
                      </span>
                    )}
                  </div>
                  <div className="challenge-actions">
                    <button
                      className="view-btn"
                      onClick={() => navigate(`/moments/${challenge.challengeId}`)}
                    >
                      {isAdmin ? 'Manage' : 'View Details'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <LoadMoreButton onClick={loadMore} loading={loadingMore} hasMore={hasMore} />
      </div>
    </div>
  );
}

